/**
 * Frontend mirror of the backend's `dashboardRoleView` map
 * (`lib/dashboard-role-view.ts`). Pure module — no React, no CSS —
 * so the root vitest suite can import it for the role-view
 * equivalence test that prevents silent drift between the backend
 * source of truth and this client fallback.
 *
 * At runtime the backend response from `GET /api/dashboard/me` is
 * authoritative. These helpers exist for redirect / loading states
 * (e.g. `DashboardHomeRedirect`) before `/me` has returned, and so
 * tests can build a `DashboardMe` fixture without hand-rolling the
 * mapping.
 *
 * `gm_agm` and `regional_head` collapse to the AGM home for the May
 * 20 pilot — matches the backend behaviour. Conservative scope until
 * broader regional access is confirmed.
 */

import type { DashboardMe, DashboardNavSection } from './types';

type SupportedRole = DashboardMe['role'];

export function dashboardHomePathForRole(role: SupportedRole): string {
  switch (role) {
    case 'owner':
      // OWNER-OPERATING-SYSTEM-V1 (HOME RULING): Home is the owner's
      // primary operating page; the old Overview path redirects.
      return '/dashboard/home';
    case 'gm_agm':
    case 'regional_head':
      return '/dashboard/agm';
    case 'coc':
      // COC-EXPERIENCE-V1 (#593 corrective): the COC home is the unbuilt
      // command center; this package seats the Follow-ups work area as a nav
      // door, not the home. The COC lands on the interim /dashboard/coc (My
      // clinics), unchanged. Mirrors backend dashboardRoleView.
      return '/dashboard/coc';
    case 'doctor':
      return '/dashboard/doctor';
    case 'receptionist':
      return '/walkin';
    // Blended-team call center lands on the command center (H1); the
    // /dashboard/lead-cockpit route renders CallcenterCommandCenter when the
    // intake signal is present. Mirrors backend dashboardRoleView.
    case 'call_center_manager':
      return '/dashboard/callcenter/workspace';
    case 'call_center_agent':
      return '/dashboard/lead-cockpit';
    case 'patient':
      return '/home';
    default: {
      const _exhaustive: never = role;
      void _exhaustive;
      return '/home';
    }
  }
}

export function dashboardNavSectionsForRole(
  role: SupportedRole,
): DashboardNavSection[] {
  switch (role) {
    case 'owner':
      return ['owner'];
    case 'gm_agm':
    case 'regional_head':
      return ['agm'];
    case 'coc':
      return ['coc'];
    case 'doctor':
      return ['doctor'];
    case 'receptionist':
      return ['receptionist'];
    // OPERATOR-JOURNEY-STITCH-V1 (V5-P9): the blended-team call center now has
    // the 'call_center' nav section (was [] -- an empty sidebar). It renders
    // the single reachable operator surface (the command center). Mirrors
    // backend dashboardRoleView.
    case 'call_center_manager':
    case 'call_center_agent':
      return ['call_center'];
    case 'patient':
      return [];
    default: {
      const _exhaustive: never = role;
      void _exhaustive;
      return [];
    }
  }
}

/**
 * `true` when the section should render for the given `DashboardMe`
 * payload. Returns `false` when `me` is null (loading or errored) so
 * partial UIs never leak sections to users who shouldn't see them.
 */
export function navCanSee(
  section: DashboardNavSection,
  me: DashboardMe | null,
): boolean {
  if (!me) return false;
  return me.navSections.includes(section);
}

/**
 * Pure access-decision for `RequireNavSection`. Returns one of:
 *  - `{ kind: 'wait' }`   while `/api/dashboard/me` is loading
 *  - `{ kind: 'redirect', to }` when the role lacks the section
 *  - `{ kind: 'render' }` when the role has the section
 *
 * On error or null `me`, redirects to `'/login'` rather than rendering
 * a role-scoped page — keeps the boundary closed without silently
 * sending lower roles to a higher-role URL.
 *
 * Tested directly in `RequireNavSection.test.tsx` so the redirect
 * targets (owner→/dashboard/owner, gm_agm→/dashboard/agm, coc→/dashboard/coc,
 * doctor→/dashboard/doctor, receptionist→/walkin) are exhaustively
 * pinned and cannot drift from the backend's `dashboardRoleView`.
 */
export type NavSectionAccessDecision =
  | { kind: 'wait' }
  | { kind: 'render' }
  | { kind: 'redirect'; to: string };

export function decideNavSectionAccess(
  section: DashboardNavSection,
  state: {
    loading: boolean;
    error: string | null;
    me: DashboardMe | null;
  },
): NavSectionAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!state.me.navSections.includes(section)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}
